import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Form, Table, Toast, ToastContainer } from 'react-bootstrap';
import './VideoTypeManagementPage.scss';
import { DIGITAL_HUMAN_TYPES, VOICE_REPRODUCTION_TYPES } from '@/constants/constants.js';
import PageContentLayout from '@/components/layout/PageContentLayout/PageContentLayout.js';
import {
  createVideoType,
  deleteVideoType,
  fetchVideoTypeList,
  updateVideoType,
} from './videoTypeService.js';

const FormField = ({ label, name, value, onChange, type = "text", required, placeholder, min, step, as, rows, colClass = "col-md-6", requiredStar = false, customLabel }) => {
  const content = (
    <Form.Group className="mb-3">
      <Form.Label>
        {customLabel || label} {requiredStar && <span className="text-danger">*</span>}
      </Form.Label>
      <Form.Control
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min}
        step={step}
        as={as}
        rows={rows}
        required={required}
      />
    </Form.Group>
  );
  return colClass ? <div className={colClass}>{content}</div> : content;
};

const FormSwitch = ({ id, label, name, checked, onChange, colClass = "col-md-12" }) => {
  const content = (
    <Form.Group className="mb-3">
      <Form.Check 
        type="switch"
        id={id}
        name={name}
        label={label}
        checked={checked}
        onChange={onChange}
      />
    </Form.Group>
  );
  return colClass ? <div className={colClass}>{content}</div> : content;
};

const VideoTypeManagementPage = () => {
  const [videoStandards, setVideoStandards] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Toast state
  const [toastInfo, setToastInfo] = useState({ show: false, message: '', variant: 'success' });
  
  const showToast = useCallback((message, variant = 'success') => {
    setToastInfo({ show: true, message, variant });
  }, []);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price_funeral: '',
    price_Cemetery: '',
    funeral_discount_price: '',
    cemetery_discount_price: '',
    version: String(new Date().getFullYear()).slice(-2),
    is_active: true,
    has_custom_price: false,
    total_images: '',
    dynamic_photos: '',
    text_word_count: '',
    subtitle_word_count: '',
    voice_reproduction: false,
  });

  const fetchStandards = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchVideoTypeList();
      if (res.data.success) {
        setVideoStandards(res.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch video standards:', error);
      showToast('获取数据失败，请重试', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchStandards();
  }, [fetchStandards]);

  const handleShowModal = (standard = null) => {
    setEditingId(standard ? standard.id : null);
    setFormData({
      title: standard?.title || '',
      description: standard?.description || '',
      price_funeral: standard?.price_funeral || '',
      price_Cemetery: standard?.price_Cemetery || '',
      funeral_discount_price: standard?.funeral_discount_price || '',
      cemetery_discount_price: standard?.cemetery_discount_price || '',
      version: standard?.version || String(new Date().getFullYear()).slice(-2),
      is_active: standard ? standard.is_active : true,
      has_custom_price: !!standard?.has_custom_price,
      total_images: standard?.total_images ? String(standard.total_images).replace(/张/g, '') : '',
      dynamic_photos: standard?.dynamic_photos ? String(standard.dynamic_photos).replace(/张/g, '') : '',
      static_photos: standard?.static_photos ? String(standard.static_photos).replace(/张/g, '') : '',
      text_word_count: standard?.text_word_count || '',
      subtitle_word_count: standard?.subtitle_word_count || '',
      subtitle_required: !!standard?.subtitle_required,
      voice_reproduction: !!standard?.voice_reproduction,
      voice_reproduction_type: standard?.voice_reproduction_type || '',
      video_length: standard?.video_length || '',
      digital_human_farewell_time: standard?.digital_human_farewell_time || '',
      digital_human_wave_time: standard?.digital_human_wave_time || '',
      ai_person_scene_fusion_time: standard?.ai_person_scene_fusion_time || '',
      ai_movie_level_scene_fusion_time: standard?.ai_movie_level_scene_fusion_time || '',
      digital_human_has: !!standard?.digital_human_has,
      digital_human_type: standard?.digital_human_type || '',
      modification_included: !!standard?.modification_included,
      modification_times: standard?.modification_times || '',
      delivery_days: standard?.delivery_days || '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newFormData = { ...formData, [name]: type === 'checkbox' ? checked : value };

    if (name === 'voice_reproduction' && !checked) {
      newFormData.voice_reproduction_type = '';
    }

    if (name === 'digital_human_type') {
      if (value === DIGITAL_HUMAN_TYPES.WAVE) {
        newFormData.digital_human_farewell_time = '';
        newFormData.ai_person_scene_fusion_time = '';
        newFormData.ai_movie_level_scene_fusion_time = '';
      } else if (value === DIGITAL_HUMAN_TYPES.ORIGINAL_VOICE) {
        newFormData.digital_human_wave_time = '';
      } else {
        newFormData.digital_human_farewell_time = '';
        newFormData.ai_person_scene_fusion_time = '';
        newFormData.ai_movie_level_scene_fusion_time = '';
        newFormData.digital_human_wave_time = '';
      }
    }

    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        price_funeral: formData.price_funeral ? parseFloat(formData.price_funeral) : null,
        price_Cemetery: formData.price_Cemetery ? parseFloat(formData.price_Cemetery) : null,
        funeral_discount_price: formData.funeral_discount_price ? parseFloat(formData.funeral_discount_price) : null,
        cemetery_discount_price: formData.cemetery_discount_price ? parseFloat(formData.cemetery_discount_price) : null,
        version: formData.version ? parseInt(formData.version) : null,
        is_active: formData.is_active,
        has_custom_price: formData.has_custom_price,
        total_images: formData.total_images ? String(formData.total_images).split('-').map(s => `${s.trim()}张`).join('-') : null,
        dynamic_photos: formData.dynamic_photos ? String(formData.dynamic_photos).split('-').map(s => `${s.trim()}张`).join('-') : null,
        static_photos: formData.static_photos ? String(formData.static_photos).split('-').map(s => `${s.trim()}张`).join('-') : null,
        text_word_count: formData.text_word_count,
        subtitle_word_count: formData.subtitle_word_count,
        subtitle_required: formData.subtitle_required,
        voice_reproduction: formData.voice_reproduction,
        voice_reproduction_type: formData.voice_reproduction_type,
        video_length: formData.video_length ? parseInt(formData.video_length) : null,
        digital_human_farewell_time: formData.digital_human_farewell_time ? parseInt(formData.digital_human_farewell_time) : null,
        digital_human_wave_time: formData.digital_human_wave_time ? parseInt(formData.digital_human_wave_time) : null,
        ai_person_scene_fusion_time: formData.ai_person_scene_fusion_time ? parseInt(formData.ai_person_scene_fusion_time) : null,
        ai_movie_level_scene_fusion_time: formData.ai_movie_level_scene_fusion_time ? parseInt(formData.ai_movie_level_scene_fusion_time) : null,
        digital_human_has: formData.digital_human_has,
        digital_human_type: formData.digital_human_type,
        modification_included: formData.modification_included,
        modification_times: formData.modification_times ? parseInt(formData.modification_times) : null,
        delivery_days: formData.delivery_days ? parseInt(formData.delivery_days) : null,
      };

      const isUpdate = !!editingId;
      const res = isUpdate 
        ? await updateVideoType(editingId, payload)
        : await createVideoType(payload);
        
      if (res.data.success) {
        showToast(isUpdate ? '更新成功' : '创建成功', 'success');
        setShowModal(false);
        fetchStandards();
      }
    } catch (error) {
      console.error('Failed to save:', error);
      showToast('保存失败，请检查填写内容或联系技术支持', 'danger');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      const res = await updateVideoType(id, {
        is_active: !currentStatus
      });
      if (res.data.success) {
        fetchStandards();
      }
    } catch (error) {
      console.error('Failed to toggle status:', error);
      showToast('操作失败', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除此标准吗？删除后不可恢复。')) return;
    try {
      const res = await deleteVideoType(id);
      if (res.data.success) {
        showToast('删除成功', 'success');
        fetchStandards();
      }
    } catch (error) {
      console.error('Failed to delete:', error);
      showToast('删除失败', 'danger');
    }
  };

  return (
    <>
      <ToastContainer position="top-center" className="p-3" style={{ zIndex: 1060, position: 'fixed' }}>
        <Toast 
          show={toastInfo.show} 
          onClose={() => setToastInfo({ ...toastInfo, show: false })} 
          delay={3000} 
          autohide
          bg={toastInfo.variant}
        >
          <Toast.Body className={toastInfo.variant === 'warning' || toastInfo.variant === 'light' ? 'text-dark' : 'text-white'}>
            {toastInfo.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <PageContentLayout
        pageClassName="video-standards-page"
        fill
        title="视频标准配置"
        actions={
          <Button variant="primary" onClick={() => handleShowModal()}>
            <i className="bi bi-plus-lg" />
            <span>新增标准</span>
          </Button>
        }
        bodyClassName="video-standards-table-container"
      >
        {loading ? (
          <div>加载中...</div>
        ) : (
          <Table striped bordered hover className="standards-table">
            <thead>
              <tr>
                <th>标准名称</th>
                <th>描述</th>
                <th>殡仪原价</th>
                <th>殡仪折扣价</th>
                <th>公墓原价</th>
                <th>公墓折扣价</th>
                <th>版本</th>
                <th>是否上架</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {videoStandards.map((std) => (
                <tr key={std.id}>
                  <td>{std.title}</td>
                  <td 
                    style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} 
                    title={std.description}
                  >
                    {std.description}
                  </td>
                  <td>{std.price_funeral || '-'}</td>
                  <td className="text-danger">{std.funeral_discount_price || '-'}</td>
                  <td>{std.price_Cemetery || '-'}</td>
                  <td className="text-danger">{std.cemetery_discount_price || '-'}</td>
                  <td>{std.version || '-'}</td>
                  <td>
                    <Form.Check 
                      type="switch"
                      id={`status-switch-${std.id}`}
                      checked={std.is_active}
                      onChange={() => handleToggleActive(std.id, std.is_active)}
                    />
                  </td>
                  <td>
                    <Button variant="outline-primary" size="sm" onClick={() => handleShowModal(std)} className="me-2">
                      编辑
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(std.id)}>
                      删除
                    </Button>
                  </td>
                </tr>
              ))}
              {videoStandards.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center">暂无数据</td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </PageContentLayout>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" scrollable dialogClassName="video-standards-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? '编辑视频标准' : '新增视频标准'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form id="video-standards-form" onSubmit={handleSubmit}>
            <h5 className="mb-3 text-primary border-bottom pb-2">基本信息</h5>
            
            <FormField 
              label="标准名称" 
              name="title" 
              value={formData.title} 
              onChange={handleChange} 
              requiredStar={true} 
              required={true}
              placeholder="例如：电影级定制、感恩送别版" 
              colClass="" 
            />
            
            <FormField 
              label="标准描述" 
              name="description" 
              value={formData.description} 
              onChange={handleChange} 
              as="textarea" 
              rows={3} 
              colClass="" 
            />

            <div className="row">
              <FormField 
                label="版本" 
                name="version" 
                value={formData.version} 
                onChange={handleChange} 
                type="number" 
                placeholder="例如：25，26" 
                requiredStar={true}
                required={true}
              />
              <FormField 
                label="交付周期（天）" 
                name="delivery_days" 
                value={formData.delivery_days} 
                onChange={handleChange} 
                type="number" 
                placeholder="例如：3" 
              />
            </div>

            <FormSwitch 
              id="is-active-switch" 
              label="上架状态" 
              name="is_active" 
              checked={formData.is_active} 
              onChange={handleChange} 
            />

            <h5 className="mt-4 mb-3 text-primary border-bottom pb-2">价格配置</h5>
            
            <FormSwitch 
              id="custom-price-switch" 
              label="定制价格" 
              name="has_custom_price" 
              checked={formData.has_custom_price} 
              onChange={handleChange} 
            />

            <div className="row">
              <FormField 
                label="殡仪原价" 
                name="price_funeral" 
                value={formData.price_funeral} 
                onChange={handleChange} 
                type="number" 
                step="0.01" 
                requiredStar={!formData.has_custom_price} 
                required={!formData.has_custom_price} 
              />
              <FormField 
                label="殡仪折扣价" 
                name="funeral_discount_price" 
                value={formData.funeral_discount_price} 
                onChange={handleChange} 
                type="number" 
                step="0.01" 
              />
            </div>

            <div className="row">
              <FormField 
                label="公墓原价" 
                name="price_Cemetery" 
                value={formData.price_Cemetery} 
                onChange={handleChange} 
                type="number" 
                step="0.01" 
                requiredStar={!formData.has_custom_price} 
                required={!formData.has_custom_price} 
              />
              <FormField 
                label="公墓折扣价" 
                name="cemetery_discount_price" 
                value={formData.cemetery_discount_price} 
                onChange={handleChange} 
                type="number" 
                step="0.01" 
              />
            </div>

            <h5 className="mt-4 mb-3 text-primary border-bottom pb-2">套餐标准</h5>

            <h6 className="mt-3 text-secondary">视频与照片信息</h6>
            <div className="row">
              <FormField 
                label="视频总时长（分钟）" 
                name="video_length" 
                value={formData.video_length} 
                onChange={handleChange} 
                type="number" 
                requiredStar={!formData.has_custom_price} 
                required={!formData.has_custom_price}
              />
              <FormField 
                label="免费修改次数" 
                name="modification_times" 
                value={formData.modification_times} 
                onChange={handleChange} 
                type="number" 
              />
            </div>

            <div className="row">
              <h6 className="mt-3 text-secondary">照片数量</h6>
              <FormField 
                label="照片总数" 
                name="total_images" 
                value={formData.total_images} 
                onChange={handleChange} 
                placeholder="例如：10 或 10-20" 
                colClass="col-md-4" 
                requiredStar={!formData.has_custom_price} 
                required={!formData.has_custom_price}
              />
              <FormField 
                label="静态照片数量" 
                name="static_photos" 
                value={formData.static_photos} 
                onChange={handleChange} 
                placeholder="例如：8 或 5-8" 
                colClass="col-md-4" 
              />
              <FormField 
                label="动态照片数量" 
                name="dynamic_photos" 
                value={formData.dynamic_photos} 
                onChange={handleChange} 
                placeholder="例如：1 或 1-3" 
                colClass="col-md-4" 
              />
            </div>

            <h6 className="mt-3 text-secondary">数字人与文案配置</h6>
            <div className="row">
              <FormSwitch 
                id="digital-human-switch" 
                label="包含数字人" 
                name="digital_human_has" 
                checked={formData.digital_human_has} 
                onChange={handleChange} 
                colClass="col-md-6 d-flex align-items-center" 
              />
              <FormField 
                label={formData.digital_human_has ? '文案字数限制' : '字幕字数限制'} 
                name={formData.digital_human_has ? 'text_word_count' : 'subtitle_word_count'} 
                value={formData.digital_human_has ? formData.text_word_count : formData.subtitle_word_count} 
                onChange={handleChange} 
                placeholder="例如：200字" 
              />
            </div>

            {formData.digital_human_has && (
              <div className="ps-3 border-start border-2 border-primary mb-3 mt-2">
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>数字人类型 <span className="text-danger">*</span></Form.Label>
                      <Form.Select 
                        name="digital_human_type" 
                        value={formData.digital_human_type} 
                        onChange={handleChange} 
                        required={formData.digital_human_has}
                      >
                        <option value="">请选择数字人类型</option>
                        <option value={DIGITAL_HUMAN_TYPES.WAVE}>{DIGITAL_HUMAN_TYPES.WAVE}</option>
                        <option value={DIGITAL_HUMAN_TYPES.ORIGINAL_VOICE}>{DIGITAL_HUMAN_TYPES.ORIGINAL_VOICE}</option>
                        <option value={DIGITAL_HUMAN_TYPES.FAREWELL}>{DIGITAL_HUMAN_TYPES.FAREWELL}</option>
                      </Form.Select>
                    </Form.Group>
                  </div>

                  {formData.digital_human_type === DIGITAL_HUMAN_TYPES.ORIGINAL_VOICE && (
                    <FormField 
                      label="原声复刻告别时长 (分钟)" 
                      name="digital_human_farewell_time" 
                      value={formData.digital_human_farewell_time} 
                      onChange={handleChange} 
                      type="number" 
                      min="0" 
                      requiredStar={!formData.has_custom_price} 
                      required={!formData.has_custom_price}
                    />
                  )}

                  {formData.digital_human_type === DIGITAL_HUMAN_TYPES.WAVE && (
                    <FormField 
                      label="挥手告别时长 (分钟)" 
                      name="digital_human_wave_time" 
                      value={formData.digital_human_wave_time} 
                      onChange={handleChange} 
                      type="number" 
                      min="0" 
                      requiredStar={!formData.has_custom_price} 
                      required={!formData.has_custom_price}
                    />
                  )}
                </div>

                {formData.digital_human_type === DIGITAL_HUMAN_TYPES.ORIGINAL_VOICE && (
                  <div className="row">
                    <FormField 
                      label="AI人物场景融合时长 (分钟) (二选一)" 
                      name="ai_person_scene_fusion_time" 
                      value={formData.ai_person_scene_fusion_time} 
                      onChange={handleChange} 
                      type="number" 
                      min="0" 
                      requiredStar={!formData.ai_person_scene_fusion_time && !formData.ai_movie_level_scene_fusion_time && !formData.has_custom_price}
                      required={!formData.ai_person_scene_fusion_time && !formData.ai_movie_level_scene_fusion_time && !formData.has_custom_price}
                    />
                    <FormField 
                      label="AI电影级分镜融合时长 (分钟) (二选一)" 
                      name="ai_movie_level_scene_fusion_time" 
                      value={formData.ai_movie_level_scene_fusion_time} 
                      onChange={handleChange} 
                      type="number" 
                      min="0" 
                      requiredStar={!formData.ai_person_scene_fusion_time && !formData.ai_movie_level_scene_fusion_time && !formData.has_custom_price}
                      required={!formData.ai_person_scene_fusion_time && !formData.ai_movie_level_scene_fusion_time && !formData.has_custom_price}
                    />
                  </div>
                )}

                <div className="row mt-2 pt-3 border-top">
                  <FormSwitch 
                    id="voice-reproduction-switch" 
                    label="需要声音复刻" 
                    name="voice_reproduction" 
                    checked={formData.voice_reproduction} 
                    onChange={handleChange} 
                    colClass="col-md-3" 
                  />
                  {formData.voice_reproduction && (
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>声音复刻类型</Form.Label>
                        <Form.Select 
                          name="voice_reproduction_type" 
                          value={formData.voice_reproduction_type} 
                          onChange={handleChange} 
                        >
                          <option value="">请选择声音复刻类型</option>
                          <option value={VOICE_REPRODUCTION_TYPES.FAMILY_AND_AI}>{VOICE_REPRODUCTION_TYPES.FAMILY_AND_AI}</option>
                          <option value={VOICE_REPRODUCTION_TYPES.AI_ONLY}>{VOICE_REPRODUCTION_TYPES.AI_ONLY}</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} className="me-2">取消</Button>
          <Button
            variant="primary"
            type="submit"
            form="video-standards-form"
          >
            保存
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VideoTypeManagementPage;
